import {
	DocumentType,
	SubmissionWithParticipantsAndValidDecennialCertificate,
} from "@dune/types";
import { Button } from "@dune/ui";
import { FileStatusChip } from "../../../../component/common/files/FileStatusChip";
import { PreviewFileLink } from "../../../../component/common/files/PreviewFileLink";
import { AddDocumentButton } from "../../../../component/constructionSites/submission/AddDocumentButton";
import { useLinkFileToConstructionSiteReception } from "../../../../api/mutations/constructionSiteReception.mutations";
import { useUnlinkFileFromConstructionSiteSubmission } from "../../../../api/mutations/constructionSiteSubmission.mutations";
import { LinkBroken02 } from "@untitled-ui/icons-react";

type RequiredDocumentsTypes = DocumentType;

interface ParticipantDocumentsReceptionProps {
	submissionParticipant: SubmissionWithParticipantsAndValidDecennialCertificate["participantToConstructionSiteSubmissions"][number];
	constructionSiteId: number;
	onDocumentClick?: (file: {
		id: string;
		key: string;
		bucket: string;
		mimeType: string;
		name: string;
	}) => void;
	loadingDocumentId?: string | null;
	disabledDocuments?: boolean;
}

const getDocumentLabel = (documentType: RequiredDocumentsTypes): string => {
	const labels: Partial<Record<RequiredDocumentsTypes, string>> = {
		ATTESTATION_DECENNALE: "Attestation Décennale",
		CONVENTION_HONORAIRES: "Convention d'honoraires",
		DEVIS: "Devis",
		FACTURE: "Facture finale",
		PV_RECEPTION: "PV de réception",
		RAPPORT_INITIAL_CONTROLE_TECHNIQUE: "Rapport",
		RAPPORT_FINAL_CONTROLE_TECHNIQUE: "Rapport final",
		RAPPORT_ETUDE_TECHNIQUE: "Rapport",
		RAPPORT_GEOTECHNIQUE: "Rapport",
		LEVEE_RESERVES_PV_RECEPTION: "Levée de réserves",
	};
	return labels[documentType] || documentType.replace(/_/g, " ").toLowerCase();
};

export const ParticipantDocumentsReception = ({
	submissionParticipant,
	constructionSiteId,
	onDocumentClick,
	loadingDocumentId,
	disabledDocuments,
}: ParticipantDocumentsReceptionProps) => {
	const requiredDocuments: RequiredDocumentsTypes[] = [];
	const participant = submissionParticipant.participant;
	const isNewParticipant =
		"isNewForReception" in participant ? participant.isNewForReception : true;

	const requiresLiftingReserves =
		"requiresLiftingReserves" in submissionParticipant
			? submissionParticipant.requiresLiftingReserves
			: false;

	const linkReceptionDoc =
		useLinkFileToConstructionSiteReception(constructionSiteId);

	if (submissionParticipant.type === "DESIGNER") {
		requiredDocuments.push("FACTURE");
		if (submissionParticipant.designerType === "CONTROLEUR_TECHNIQUE") {
			requiredDocuments.push("RAPPORT_FINAL_CONTROLE_TECHNIQUE");
		}
		if (isNewParticipant) {
			requiredDocuments.push("ATTESTATION_DECENNALE");
		}
	} else if (submissionParticipant.type === "BUILDER") {
		requiredDocuments.push("FACTURE", "PV_RECEPTION");
		if (isNewParticipant) {
			requiredDocuments.push("ATTESTATION_DECENNALE");
		}
	} else if (submissionParticipant.type === "MATERIAL") {
		requiredDocuments.push("FACTURE");
	}

	if (requiresLiftingReserves) {
		requiredDocuments.push("LEVEE_RESERVES_PV_RECEPTION");
	}

	const getReceptionFilesForType = (documentType: RequiredDocumentsTypes) =>
		participant.filesOnConstructionSiteReception
			?.filter((file) => file.type === documentType)
			.map((file) => file.File) || [];

	const findFilesForType = (documentType: RequiredDocumentsTypes) => {
		const files = [...getReceptionFilesForType(documentType)];

		if (documentType === "ATTESTATION_DECENNALE") {
			files.push(
				...(submissionParticipant.decennialCertificatesOfSubmissionOrValidFromOthers?.map(
					(cert) => cert.file,
				) || []),
			);
		}
		return files;
	};

	const unlinkFileMutation =
		useUnlinkFileFromConstructionSiteSubmission(constructionSiteId);

	return (
		<div className="flex flex-col gap-4">
			{requiredDocuments.map((documentType) => {
				const files = findFilesForType(documentType);
				return (
					<div key={documentType} className="flex flex-col gap-2">
						<p className="text-sm font-medium">
							{getDocumentLabel(documentType)}
						</p>
						<div className="flex flex-col gap-2">
							{files.length > 0
								? files.map((file) => (
										<div
											key={file.id}
											className="flex gap-2 items-center justify-between min-w-0"
										>
											{onDocumentClick ? (
												<Button
													variant="link"
													type="button"
													size="sm"
													className="text-blue-600 p-0 h-auto font-normal justify-start flex-1 min-w-0 overflow-hidden"
													onClick={() => onDocumentClick(file)}
													disabled={loadingDocumentId === file.id}
												>
													<span className="truncate w-0 flex-1">{file.name}</span>
													{loadingDocumentId === file.id && " (Chargement...)"}
												</Button>
											) : (
												<PreviewFileLink
												file={file}
												className="flex-1 overflow-hidden"
											/>
											)}
											<div className="flex items-center shrink-0">
												<FileStatusChip
													humanProcessingStatus={file.humanProcessingStatus}
												/>
												{!disabledDocuments && (
													<Button
														variant="ghost"
														size="sm"
														onClick={() => {
															unlinkFileMutation.mutate(file.id);
														}}
													>
														<LinkBroken02 className="h-4 w-4" />
													</Button>
												)}
											</div>
										</div>
									))
								: null}
							<div className="flex justify-start">
								{!disabledDocuments && (
									<AddDocumentButton
										constructionSiteId={constructionSiteId}
										onUploadSuccess={(fileId) => {
											linkReceptionDoc.mutate({
												fileId,
												type: documentType,
												participantId: participant.id,
											});
										}}
									/>
								)}
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
};
